"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import log from "logbeacon";
import {
  KEYBOARD_INSETS_IDLE,
  applyKeyboardInsetToDom,
  buildKeyboardInsetsState,
  getKeyboardInsetBottomFromCSS,
  isKeyboardInsetsSupported,
  mergeKeyboardInsetsState,
  parseKeyboardInsetsPayload,
  resolveDefaultKeyboardLayoutMode,
  shouldSkipKeyboardInsetsReactUpdate,
  type KeyboardInsetsState,
} from "@/src/lib/bridge/keyboardInsets";
import { MessageType } from "@/src/lib/bridge/types";
import { useAppFeatures, useBridge } from "@/src/lib/bridge/useBridge";

const ENABLE_KEYBOARD_INSETS_DEBUG = process.env.NEXT_PUBLIC_ENV !== "prod";

/**
 * 读取原生键盘 inset（仅 iOS Bridge；Android 恒为 idle）。
 *
 * - 高度写入 `--keyboard-inset-bottom` 在 Web 端完成（`applyKeyboardInsetToDom`），
 *   供 `pb-keyboard-real` 等 CSS 使用；原生不再 evaluateJavaScript 写 CSS。
 * - `keyboardWebInset === true`（Phase 2）：`shouldApplyWebInsetPadding` 在键盘可见时为 true。
 * - will/did 全生命周期；输入中仅高度变大时 did-show + peak CSS（不触发 React，见 shouldSkip）。
 */
export function useKeyboardInsets(): KeyboardInsetsState {
  const appFeatures = useAppFeatures();
  const { on, platform } = useBridge();
  const bridgeEnabled = isKeyboardInsetsSupported(platform, appFeatures);
  const defaultLayoutMode = resolveDefaultKeyboardLayoutMode(appFeatures);
  const defaultLayoutModeRef = useRef(defaultLayoutMode);
  defaultLayoutModeRef.current = defaultLayoutMode;

  const [state, setState] = useState<KeyboardInsetsState>(() =>
    bridgeEnabled
      ? buildKeyboardInsetsState(true, {
          visible: false,
          height: 0,
          layoutMode: defaultLayoutMode,
        })
      : KEYBOARD_INSETS_IDLE,
  );

  const bridgeEnabledRef = useRef(bridgeEnabled);
  bridgeEnabledRef.current = bridgeEnabled;

  const syncInsetCss = useCallback(
    (data: unknown, defaultVisible?: boolean) => {
      const parsed = parseKeyboardInsetsPayload(data, { defaultVisible });
      if (!parsed) {
        return;
      }
      applyKeyboardInsetToDom(parsed.visible ? parsed.height : 0);
    },
    [],
  );

  const applyPayload = useCallback(
    (data: unknown, defaultVisible?: boolean) => {
      if (!bridgeEnabledRef.current) {
        return;
      }
      syncInsetCss(data, defaultVisible);
      const parsed = parseKeyboardInsetsPayload(data, { defaultVisible });
      if (!parsed) {
        return;
      }
      if (!parsed.layoutMode) {
        parsed.layoutMode = defaultLayoutModeRef.current;
      }
      setState((prev) => {
        const next = mergeKeyboardInsetsState(prev, parsed);
        if (shouldSkipKeyboardInsetsReactUpdate(prev, next)) {
          return prev;
        }
        if (ENABLE_KEYBOARD_INSETS_DEBUG) {
          log.info("[KeyboardInsets] update", next);
        }
        return next;
      });
    },
    [syncInsetCss],
  );

  useEffect(() => {
    if (!bridgeEnabled) {
      setState(KEYBOARD_INSETS_IDLE);
      return;
    }

    const layoutMode = defaultLayoutModeRef.current;

    setState(
      buildKeyboardInsetsState(true, {
        visible: false,
        height: getKeyboardInsetBottomFromCSS(),
        layoutMode,
      }),
    );

    const hidePayload = () => ({
      visible: false as const,
      height: 0,
      layoutMode: defaultLayoutModeRef.current,
    });

    // will-*：动画开始时提前写 CSS / 显隐；did-*：落定后再对齐一次
    const unsubWillShow = on(MessageType.KEYBOARD_WILL_SHOW, (data) =>
      applyPayload(data, true),
    );
    const unsubWillHide = on(MessageType.KEYBOARD_WILL_HIDE, () =>
      applyPayload(hidePayload()),
    );
    const unsubDidShow = on(MessageType.KEYBOARD_DID_SHOW, (data) =>
      applyPayload(data, true),
    );
    const unsubDidHide = on(MessageType.KEYBOARD_DID_HIDE, () =>
      applyPayload(hidePayload()),
    );

    return () => {
      unsubWillShow();
      unsubWillHide();
      unsubDidShow();
      unsubDidHide();
    };
  }, [applyPayload, bridgeEnabled, defaultLayoutMode, on]);

  return bridgeEnabled ? state : KEYBOARD_INSETS_IDLE;
}
